/**
 * World — binds a LevelFormat to Pixi rendering and OWNS a {@link Sim} it delegates the step to.
 * Owns: tile sprites, background (parallax), player/enemy/goal sprites, camera, the juice/particle/
 * filter render layers, and the death-POLICY (respawn vs one-life freeze). Rendering keys off
 * LOGICAL manifest keys only (skins = swap manifest).
 *
 * Phase C: the deterministic sim step used to live INSIDE this class (a 1296-line Pixi-coupled
 * `update`) and was hand-mirrored elsewhere. It now lives in the Pixi-free `Sim` core; `World`
 * supplies a {@link SimFx} (the `fx` field) that turns every sim event into its render/audio
 * effect, calls `sim.step`, and reads `sim`'s public state to position sprites. `World.update`
 * is behaviour-preserving over the old one — same inputs → same frames (golden.test.ts).
 */
import { Application, Assets, Container, Graphics, Sprite, Text, Texture, TilingSprite } from 'pixi.js';
import { resolveComposite } from '../assets/AssetManifest';
import { Camera } from './Camera';
import type { Entity } from './Entity';
import { type DeathCause, deathCell } from './deathCell';
import { type ArchetypeDef, type ArchetypeId, ARCHETYPES, resolveArchetype } from './Archetype';
import type { InputSnapshot } from './Input';
import { type ActiveMutators, type MutatorId, NO_MUTATORS, resolveMutators } from './Mutators';
import { computeLevelId, EntityType, type LevelEntity, type LevelFormat, TileType } from './LevelFormat';
import { pickTileKey } from './tileAutotile';
import {
  type ClearProgress,
  conditionLabel,
  formatTime,
  timeRemainingSteps,
} from './clearConditions';
import type { Rng } from './Rng';
import type { Player } from './Player';
import type { TileGrid } from './TileGrid';
import { Sim, type SimFx } from './Sim';
import { Sound } from './Sound';
import { Juice } from '../fx/Juice';
import { ParticleFx } from '../fx/ParticleFx';
import { FilterFx } from '../fx/FilterFx';

/** Coin-chain: collects within this many fixed steps keep ramping the pitch (≈1.25 s). */
const COIN_CHAIN_WINDOW_STEPS = 75;

/** POLISH_02 #7: the far sky layer scrolls slower than bg_far (tunables.bgParallax) for depth. */
const SKY_PARALLAX = 0.1;

/**
 * POLISH_02 #4: region-hazard/zone entity types whose `renderSize()` box gets a repeating
 * TilingSprite (one 64px art tile per cell) instead of a stretched Sprite (which smeared the
 * source band art 3-10x — Lava.ts:72 + World.render's old s.width/s.height-on-a-plain-Sprite
 * path). Scoped to these 4 named types only — the doc's fix + evidence call out LAVA/FAN/
 * EXITGATE (all Brief 012/014 tile-aligned ZONES) plus DREAMBLOCK explicitly; the other
 * renderSize() users (Platform/Conveyor/Warp door/Laser/Projectile) are thin bars or already
 * read fine stretched, so their plain-Sprite path is untouched per the task's scope fence. */
const TILING_REGION_TYPES: ReadonlySet<EntityType> = new Set([
  EntityType.LAVA, EntityType.FAN, EntityType.EXITGATE, EntityType.DREAMBLOCK,
]);

/** POLISH_02 #6: DECOR variant → sprite key (props.variant, mirrors FLYER/WALKER/ITEM). Falls
 *  back to 'decor_bush' for an unknown/missing variant (never renders nothing). */
const DECOR_SPRITE_KEYS: Record<string, string> = {
  bush: 'decor_bush', rock: 'decor_rock', fence: 'decor_fence', sign: 'decor_sign',
  cloud: 'decor_cloud', mushroom: 'decor_mushroom', grasstuft: 'decor_grasstuft',
};

/** Pitch multiplier for the n-th coin in a chain: n semitones up (2^(1/12) each), capped at
 *  an octave. Repeated multiply, NOT `**`/Math.pow (both banned in src/engine). */
function coinChainPitch(n: number): number {
  const semis = n > 12 ? 12 : n;
  let p = 1;
  for (let i = 0; i < semis; i++) p *= 1.05946; // 2^(1/12)
  return p;
}

/** Live render-only knobs, exposed to the debug panel (do not affect physics). */
export interface RenderTunables {
  playerFootOffset: number; // px nudge so sprite feet align to the body bottom
  bgParallax: number;
}

export class World {
  /** The Pixi-free deterministic core this World delegates its step to (public so a ghost /
   *  the golden harness can read sim state; never mutated from outside). */
  readonly sim: Sim;
  readonly camera: Camera;
  readonly sound = new Sound();
  /** When false, the in-canvas Pixi LEVEL CLEAR! / GAME OVER banners are suppressed so the DOM
   *  result screen owns that moment (the editor playtest leaves this true). */
  showResultBanner = true;
  /** Tier 3 (Feature H), one-life mutator: a death ENDS the run (no respawn) and freezes the sim
   *  in a fail state until manual reset (R). false in every other category. World-owned POLICY:
   *  the Sim runs `deathPolicy: 'terminal'` under one-life and `fx.onFreeze` flips this. */
  failed = false;
  tunables: RenderTunables = { playerFootOffset: 8, bgParallax: 0.3 };
  /**
   * Tier 2 death-heatmap hook (Feature F). Fired from the REAL-death sim paths (via fx.onDeath →
   * reportDeath) with the tile cell the death is attributed to. Not fired from respawn(). The
   * loop owner (main.ts) buffers these + best-effort POSTs them for a published level. */
  onDeath?: (x: number, y: number, cause: DeathCause) => void;

  // Pixi layers
  private bgLayer = new Container();
  private worldLayer = new Container();
  private uiLayer = new Container();
  private bg?: TilingSprite;
  /** POLISH_02 #7: the farther sky parallax layer (bg_sky), behind bg_far. */
  private sky?: TilingSprite;
  private playerSprite!: Sprite;
  /** COMBAT held sword (procedural; ALWAYS visible on a combat level, swings on attack). */
  private swordFx?: Graphics;
  /** COMBAT sword-swing crescent (procedural; flashes over the active attack window). */
  private slashFx?: Graphics;
  /** COMBAT dash-kill motion streak (procedural; shown while a dash is live). */
  private dashFx?: Graphics;
  private goalSprite?: Sprite;
  private clearBanner!: Text;
  /** Tier 3 (Feature H): the "GAME OVER — press R" banner (one-life run-end only). */
  private failBanner!: Text;

  private tex = new Map<string, Texture>();
  /** Tiles are static (set once) → tracked so swapSkin can rebind them. */
  private tileSprites: { sprite: Sprite; key: string }[] = [];
  /** POLISH_02 #6: render-only DECOR sprites (built straight from level.entities[], NOT
   *  sim.entities — the sim never spawns a DECOR body). Static like tiles → tracked so
   *  swapSkin can rebind them. */
  private decorSprites: { sprite: Sprite; key: string }[] = [];
  /** Sprites for the sim's entities, kept PARALLEL to `sim.entities` by index (World owns the
   *  render halves of the sim's build/spawn via fx.onRespawn + fx.onSpawn). A region-zone entity
   *  (POLISH_02 #4 — TILING_REGION_TYPES) gets a TilingSprite instead of a plain Sprite; both
   *  share `.texture`/`.visible`/`.position`/`.scale`, so the generic per-frame render loop below
   *  needs no type-branch beyond the ONE place that decides which to construct. */
  private entitySprites: (Sprite | TilingSprite)[] = [];
  private flagAnim = 0;

  /** Render-only juice (screenshake / squash / hit-flash / screen-flash / hitstop). NEVER read
   *  by the sim → replay-safe. */
  readonly juice = new Juice();
  /** Fullscreen white flash overlay (big-moment juice); alpha set each render from juice. */
  private flashRect!: Sprite;
  /** Coin-chain arpeggio state (audio-only): consecutive collects within a window ramp the pitch
   *  up. stepCount-keyed → deterministic; never read by the sim. */
  private coinChainStep = -999;
  private coinChainCount = 0;
  /** Pooled render-only particles (dust/sparkle/burst/floating-text/dash-afterimages). */
  readonly particles = new ParticleFx();
  /** Transient screen filters (shockwave/RGB-split) on the world layer, bounded to viewport. */
  readonly filterFx = new FilterFx(this.worldLayer);
  /** Render-only 2-frame counter spacing out dash afterimages. */
  private afterTick = 0;
  /** POLISH_01 #2 (death beat): the death-SITE red burst is queued here in fx.onDeath and spawned
   *  in render() AFTER the in-step respawn ran particles.clear() (so it survives the wipe). */
  private pendingDeathBurst: { x: number; y: number } | null = null;
  /** POLISH_01 #2: set by a real death so fx.onRespawn SKIPS the hard camera snap to the
   *  checkpoint — the camera holds near the death site (the frozen hitstop frame) and then eases
   *  back to the player (via the #3 X-smoothing). Consumed (cleared) by the next onRespawn. */
  private holdDeathCam = false;
  /** POLISH_01 #8: render-frame spacer for ongoing skid dust (the onset SFX is edge-fired). */
  private skidTick = 0;
  /** POLISH_01 #5: last whole-second the low-time countdown ticked (so it beeps once per second). */
  private lastTickSecond = -1;

  /** The theme currently bound to the renderer (may differ from level.theme under a player skin). */
  currentTheme: string;
  /** Themes already registered as a Pixi Assets bundle. STATIC so it survives across World
   *  instances (the editor builds a fresh World per playtest; addBundle must not run twice). */
  private static bundlesAdded = new Set<string>();
  /** POLISH_02 #9: the current theme's resolved bespoke track URL, or null when the theme has
   *  none (most themes) — set by loadThemeTextures from the resolved manifest. Render-side only
   *  (the sim never reads this); main.ts reads {@link musicKey} after World.load to decide
   *  whether to point the Music player at this theme's track or the global PLAY_TRACKS rotation. */
  private themeMusicKey: string | null = null;
  /** The active theme's bespoke music track URL, or null → caller should fall back to
   *  the global PLAY_TRACKS rotation (see main.ts). */
  get musicKey(): string | null { return this.themeMusicKey; }

  /**
   * The render sink handed to the Sim: every sim event → its cosmetic effect (juice/particles/
   * filters/sound) + the sprite/camera/policy consequences. The sim never READS any of this, so
   * it can't perturb replay parity. All hooks are lazy arrows — they only touch render members
   * (some built in buildUI) when CALLED, which is always after load().
   */
  private readonly fx: SimFx = {
    onJump: () => {
      this.sound.play('jump', 0.45);
      this.juice.squash(0.9, 1.12); // takeoff stretch
      const b = this.player.body;
      this.particles.dust(b.x + b.w / 2, b.y + b.h, 3);
    },
    onLand: (vy) => this.onPlayerLand(vy),
    onBump: () => this.sound.play('bump', 0.3),
    onCoin: (stepCount) => {
      // Coin-chain arpeggio: consecutive collects within the window ramp the pitch a semitone
      // each (deterministic — keyed off the fixed-step counter, so a replay reproduces the tune).
      this.coinChainCount = (stepCount - this.coinChainStep <= COIN_CHAIN_WINDOW_STEPS)
        ? this.coinChainCount + 1 : 0;
      this.coinChainStep = stepCount;
      this.sound.play('coin', 0.5, coinChainPitch(this.coinChainCount));
    },
    onKill: (x, y) => {
      this.juice.addTrauma(0.13); this.juice.hitstop(35); // kill punch
      this.particles.burst(x, y, 9, 0xffffff);
      this.filterFx.shockwave(x - this.camera.x, y - this.camera.y); // ripple at screen pos
    },
    onHurt: () => {
      this.sound.play('hurt', 0.5);
      this.juice.hitFlash(1);
      this.juice.addTrauma(0.35);
      this.juice.hitstop(45);
      this.filterFx.hurt();
    },
    onDeath: (cause, dcx, dcy) => {
      // Death juice — the heaviest hit: big flash, hard shake, longest freeze, a DOWN-pitched
      // splat sound, then attribute the death cell for the heatmap. (deaths++ lives in the Sim.)
      this.juice.screenFlashPulse(0.85);
      this.juice.addTrauma(0.7);
      this.juice.hitstop(160);
      this.sound.play('hurt', 0.55, 0.5);
      // POLISH_01 #2 (death beat): burst at the DEATH SITE (queued for the next render so it
      // survives the in-step respawn's particles.clear) and hold the camera there for the freeze.
      this.pendingDeathBurst = { x: dcx, y: dcy };
      this.holdDeathCam = true;
      this.reportDeath(cause);
    },
    onRespawnSplat: (x, y) => { this.particles.burst(x, y, 6, 0xff3b46); }, // small puff at the respawn point
    onFreeze: (x, y) => {
      // One-life: no respawn — splat where the player fell, enter the fail state (the sim froze).
      this.particles.burst(x, y, 16, 0xff3b46);
      this.failed = true;
      if (this.showResultBanner) this.failBanner.visible = true;
    },
    onRespawn: (cx, feetY) => {
      // The render half of a respawn (the Sim did the gameplay reset): re-sync entity sprites,
      // drop in-flight particles, hide banners, clear the fail state, snap the camera.
      this.rebuildEntitySprites();
      this.particles.clear();
      this.clearBanner.visible = false;
      this.failed = false;
      if (this.failBanner) this.failBanner.visible = false;
      // POLISH_01 #2: on a REAL death, don't snap to the checkpoint — the frozen hitstop frame
      // should show the death site (the camera is still there), then ease back via #3 X-smoothing.
      // A manual reset / warp / load (holdDeathCam false) still snaps instantly.
      if (this.holdDeathCam) {
        this.holdDeathCam = false;
      } else {
        this.camera.follow(cx, feetY, 1);
        this.camera.snap();
      }
    },
    onClear: () => {
      if (this.showResultBanner) this.clearBanner.visible = true;
      // POLISH_01 #7 — an in-world CLEAR beat: a burst + white pulse at the goal flag and a
      // layered fanfare (two pitched clips) so winning no longer plays the lone coin blip. The
      // 700 ms delay before the result card is DOM-side in main.ts (replay-safe).
      const gx = this.goalSprite ? this.goalSprite.x : this.player.body.x + this.player.body.w / 2;
      const gy = (this.goalSprite ? this.goalSprite.y : this.player.body.y) - 40;
      this.particles.burst(gx, gy, 20, 0xffe66d);
      this.juice.screenFlashPulse(0.4);
      this.sound.play('fanfare', 0.6); // POLISH_01 #1/#7 — dedicated victory jingle (was a lone coin blip)
    },
    onWarp: () => {
      this.sound.play('select', 0.5);
      const p = this.player.body;
      this.camera.follow(p.x + p.w / 2, p.y + p.h / 2, 1); // snap to the destination
      this.camera.snap();
    },
    onSpawn: (e) => this.addEntitySprite(e),
    playSound: (k, v, p) => this.sound.play(k, v, p),
    emitFx: (kind, x, y, opts) => this.emitFx(kind, x, y, opts),
    onDash: (dir) => {
      // POLISH_01 #4 — the flagship verb had zero audio + zero kick. Whoosh (pitched-down jump as a
      // placeholder), a shake, and a trailing dust kick OPPOSITE the dash direction.
      this.sound.play('jump', 0.4, 0.7);
      this.juice.addTrauma(0.18);
      const b = this.player.body;
      this.particles.dust(b.x + b.w / 2, b.y + b.h * 0.5, 4, -dir);
    },
    onSkid: () => {
      // POLISH_01 #8 — run→turn→stop was silent + invisible. A short high screech + a dust kick.
      this.sound.play('bump', 0.16, 1.5);
      const b = this.player.body;
      this.particles.dust(b.x + b.w / 2, b.y + b.h, 4, -this.player.facing);
    },
    onLaunch: (dx, dy) => {
      // POLISH_01 #10 — squash the body ALONG the fling axis (tall+thin for a vertical launch,
      // wide+short for a horizontal one) + a little trauma. Math.abs is render-side (unlinted).
      this.juice.addTrauma(0.1);
      const ax = Math.abs(dx), ay = Math.abs(dy);
      if (ay >= ax) this.juice.squash(0.78, 1.32);
      else this.juice.squash(1.32, 0.78);
    },
  };

  private constructor(
    private app: Application,
    private level: LevelFormat,
    /** Active rule-variant category (Tier 3, Feature H). NO_MUTATORS = vanilla. */
    readonly mutators: ActiveMutators = NO_MUTATORS,
    /** Authored movement archetype (CLASSIC default). Its feel PROFILE + verbs + offense rule
     *  are applied inside the Sim. */
    readonly archetype: ArchetypeDef = ARCHETYPES.classic,
  ) {
    this.currentTheme = level.theme;
    // The deterministic core. One-life maps to a 'terminal' death policy so `fx.onFreeze` ends
    // the run (no respawn); every other category respawns in place.
    this.sim = new Sim(level, {
      archetype: archetype.id,
      mutators,
      fx: this.fx,
      deathPolicy: mutators.oneLife ? 'terminal' : 'respawn',
    });
    this.camera = new Camera(app.screen.width, app.screen.height, this.sim.grid.widthPx, this.sim.grid.heightPx);
    app.stage.addChild(this.bgLayer, this.worldLayer, this.uiLayer);
  }

  // ── Gameplay state — delegated to the Sim (World keeps only render/policy state) ──
  get grid(): TileGrid { return this.sim.grid; }
  get player(): Player { return this.sim.player; }
  get rng(): Rng { return this.sim.rng; }
  get initialSeed(): number { return this.sim.initialSeed; }
  get cleared(): boolean { return this.sim.cleared; }
  get hearts(): number { return this.sim.hearts; }
  get maxHearts(): number { return this.sim.maxHearts; }
  get iframeTimer(): number { return this.sim.iframeTimer; }
  get invulnSteps(): number { return this.sim.invulnSteps; }
  get score(): number { return this.sim.score; }
  get coins(): number { return this.sim.coins; }
  get placedCoinsCollected(): number { return this.sim.placedCoinsCollected; }
  get totalCoins(): number { return this.sim.totalCoins; }
  get totalEnemies(): number { return this.sim.totalEnemies; }
  get enemiesKilled(): number { return this.sim.enemiesKilled; }
  get timeLimitSteps(): number { return this.sim.timeLimitSteps; }
  get stepCount(): number { return this.sim.stepCount; }
  get goalHint(): string { return this.sim.goalHint; }
  get respawnGen(): number { return this.sim.respawnGen; }
  get deaths(): number { return this.sim.deaths; }
  /** POLISH_03 #1: current respawn point is the PLAYER_START (no active checkpoint) — the loop
   *  owner only banks/submits a replay while this is true. */
  get respawnIsStart(): boolean { return this.sim.atStartRespawn; }

  /**
   * Landing squash (render-only juice): a fall-speed-scaled wide/short splat + a low thud on a
   * hard landing only. impactVy is +down px/s, captured before the sim zeroed it. */
  private onPlayerLand(impactVy: number): void {
    let k = impactVy / 3500;
    if (k < 0.06) k = 0.06;
    if (k > 0.30) k = 0.30;
    this.juice.squash(1 + k, 1 - k); // wide + short; spring recovers to (1,1)
    const b = this.player.body;
    this.particles.dust(b.x + b.w / 2, b.y + b.h, impactVy > 800 ? 6 : 3); // feet dust
    if (impactVy > 1200) this.sound.play('bump', 0.22, 0.8); // low thud on a real drop
    else if (impactVy > 500) this.sound.play('bump', 0.1, 1.15); // soft high touch-down tick
  }

  /** Render-only fx emit routed from entities via ctx.emitFx (cosmetic; never affects the sim). */
  private emitFx(kind: 'sparkle' | 'pop' | 'burst' | 'dust', x: number, y: number, opts?: { text?: string; color?: number; count?: number; dir?: number }): void {
    switch (kind) {
      case 'sparkle': this.particles.sparkle(x, y, opts?.color ?? 0xffe66d); break;
      case 'pop': this.particles.pop(x, y, opts?.text ?? '', opts?.color ?? 0xffffff); break;
      case 'burst': this.particles.burst(x, y, opts?.count ?? 9, opts?.color ?? 0xffffff); break;
      case 'dust': this.particles.dust(x, y, opts?.count ?? 5, opts?.dir ?? 0); break;
    }
  }

  /** Accessibility: toggle ALL render-only juice (shake/flash/hitstop/particles/filters). */
  setReducedFx(on: boolean): void {
    this.juice.reducedFx = on;
    this.particles.reduced = on;
    this.filterFx.reduced = on;
  }

  /** Async factory: resolve theme manifest, load assets, build the scene. */
  static async load(
    app: Application,
    level: LevelFormat,
    theme: string = level.theme,
    mutatorIds: Iterable<unknown> = [],
    archetype: unknown = level.meta.archetype,
  ): Promise<World> {
    const w = new World(app, level, resolveMutators(mutatorIds), resolveArchetype(archetype));
    w.currentTheme = theme;
    await w.loadThemeTextures(theme);
    w.buildBackground();
    w.buildDecorSprites(); // POLISH_02 #6: added BEFORE tiles/entities so decor sits behind both
    w.buildTiles();
    w.buildEntities();
    w.buildUI();
    w.respawn();
    return w;
  }

  /**
   * Register a theme's sprite URLs as a Pixi Assets bundle, load it, bind every logical key →
   * Texture into this.tex, and (re)load the theme's SFX. Shared by the initial load and swapSkin.
   */
  private async loadThemeTextures(theme: string): Promise<void> {
    // `theme` may be a plain theme name OR a palette signature (base|cat=theme|…) — the
    // per-category personal skin mix. resolveComposite handles both (plain = empty palette).
    const m = resolveComposite(theme);
    if (!World.bundlesAdded.has(theme)) {
      const urlMap: Record<string, string> = {};
      for (const url of Object.values(m.sprites)) urlMap[url] = url;
      Assets.addBundle(theme, urlMap);
      World.bundlesAdded.add(theme);
    }
    await Assets.loadBundle(theme);
    for (const [key, url] of Object.entries(m.sprites)) {
      const t = (Assets.get(url) as Texture) ?? Texture.from(url);
      t.source.scaleMode = 'nearest'; // crisp pixel art — v8 does NOT persist this across (re)load
      this.tex.set(key, t);
    }
    this.sound.load(m.sounds);
    this.themeMusicKey = m.musicKey; // POLISH_02 #9: theme-specific track (or null → PLAY_TRACKS)
  }

  /**
   * Runtime hot-swap to a different theme: load the NEW bundle → rebind every logical texture +
   * SFX → rebind static sprite holders. No-op if already on `theme`. We deliberately do NOT
   * unload the old bundle (shared fallback textures would be destroyed under live sprites).
   */
  async swapSkin(theme: string): Promise<void> {
    if (theme === this.currentTheme) return;
    await this.loadThemeTextures(theme);
    this.currentTheme = theme;
    this.rebindAllSprites();
  }

  /** Point every sprite holder at this.tex's current textures (tiles/bg are textured once → must
   *  be rebound; player/enemy/goal are re-read each render but rebound too, so no holder still
   *  references the OLD bundle when it might be freed). */
  private rebindAllSprites(): void {
    for (const { sprite, key } of this.tileSprites) {
      const t = this.tex.get(key);
      if (t) sprite.texture = t;
    }
    for (const { sprite, key } of this.decorSprites) { // POLISH_02 #6
      const t = this.tex.get(key);
      if (t) sprite.texture = t;
    }
    const far = this.tex.get('bg_far');
    if (this.bg && far) {
      this.bg.texture = far;
      this.bg.tileScale.set(this.app.screen.height / far.height);
    }
    const sky = this.tex.get('bg_sky'); // POLISH_02 #7: rebind the parallax sky on a skin swap too
    if (this.sky && sky) {
      this.sky.texture = sky;
      this.sky.tileScale.set(this.app.screen.height / sky.height);
    }
    const pt = this.tex.get(this.playerSpriteKey());
    if (this.playerSprite && pt) this.playerSprite.texture = pt;
    const ents = this.sim.entities;
    for (let i = 0; i < ents.length; i++) {
      const t = this.tex.get(ents[i].spriteKey());
      if (t) this.entitySprites[i].texture = t;
    }
    const gt = this.tex.get('goal_flag_a');
    if (this.goalSprite && gt) this.goalSprite.texture = gt;
  }

  private buildBackground(): void {
    // POLISH_02 #7: the far SKY layer (bg_sky) sits behind bg_far and scrolls slower → real parallax
    // depth. The asset is REQUIRED + vendored in every theme but was never drawn until now.
    const sky = this.tex.get('bg_sky');
    if (sky) {
      const s = new TilingSprite({ texture: sky, width: this.app.screen.width, height: this.app.screen.height });
      s.tileScale.set(this.app.screen.height / sky.height);
      this.sky = s;
      this.bgLayer.addChild(s); // added FIRST → farthest back (behind bg_far)
    }
    const far = this.tex.get('bg_far');
    if (!far) return;
    const bg = new TilingSprite({ texture: far, width: this.app.screen.width, height: this.app.screen.height });
    const scale = this.app.screen.height / far.height;
    bg.tileScale.set(scale);
    this.bg = bg;
    this.bgLayer.addChild(bg);
  }

  /**
   * POLISH_02 #6: render-only decoration, built DIRECTLY from `level.entities[]` (never
   * `sim.entities` — the sim spawns nothing for DECOR; see spawnEntityAtPx). Static sprites
   * (no per-frame animation), added to `worldLayer` BEFORE tiles/entities so they draw behind
   * both. `props.variant` selects the art via {@link DECOR_SPRITE_KEYS}; an unknown/missing
   * variant falls back to the bush key rather than rendering nothing.
   */
  private buildDecorSprites(): void {
    const ts = this.grid.ts;
    this.decorSprites = [];
    for (const e of this.level.entities) {
      if (e.type !== EntityType.DECOR) continue;
      const variant = typeof e.props?.variant === 'string' ? e.props.variant : 'bush';
      const key = DECOR_SPRITE_KEYS[variant] ?? 'decor_bush';
      const tex = this.tex.get(key);
      if (!tex) continue;
      const s = new Sprite(tex);
      s.anchor.set(0.5, 1);
      s.position.set(e.x * ts + ts / 2, (e.y + 1) * ts);
      this.worldLayer.addChild(s);
      this.decorSprites.push({ sprite: s, key });
    }
  }

  private buildTiles(): void {
    const ts = this.grid.ts;
    this.tileSprites = [];
    for (let row = 0; row < this.grid.rows; row++) {
      for (let col = 0; col < this.grid.cols; col++) {
        const t = this.grid.tileAt(col, row);
        let key: string | null = null;
        // POLISH_02 #5: 4-bit neighbor-mask autotile (top/left/right) instead of the old
        // top-vs-fill 2-way pick — edge caps + corners on a SOLID run (pure fn: tileAutotile.ts).
        if (t === TileType.SOLID) {
          key = pickTileKey({
            top: this.grid.isSolid(col, row - 1),
            left: this.grid.isSolid(col - 1, row),
            right: this.grid.isSolid(col + 1, row),
          });
        } else if (t === TileType.SEMISOLID) key = 'tile_semisolid';
        if (!key) continue;
        const tex = this.tex.get(key);
        if (!tex) continue;
        const s = new Sprite(tex);
        s.width = ts; s.height = ts;
        s.position.set(col * ts, row * ts);
        this.worldLayer.addChild(s);
        this.tileSprites.push({ sprite: s, key });
      }
    }
  }

  /**
   * Build the RENDER halves of the scene entities: the goal-flag sprite + a parallel sprite per
   * sim entity + the player sprite + the combat visuals + the particle layer. The sim owns the
   * entity list / warp points / goal box; here we only make sprites.
   */
  private buildEntities(): void {
    const ts = this.grid.ts;
    for (const e of this.level.entities) {
      if (e.type === EntityType.GOAL_FLAG) {
        this.buildGoalSprite(e, e.x * ts + ts / 2, (e.y + 1) * ts);
      }
    }
    this.rebuildEntitySprites();
    // player sprite
    this.playerSprite = new Sprite(this.tex.get('player_small_idle'));
    this.playerSprite.anchor.set(0.5, 1);
    this.worldLayer.addChild(this.playerSprite);
    this.buildCombatFx();
    // Juice particle layer ON TOP of world content.
    this.worldLayer.addChild(this.particles.layer);
  }

  /**
   * Build the COMBAT swing/dash visuals once (procedural Graphics, render-only). Inert for
   * non-COMBAT forms (radius derives from attackHitboxReach; visible only while a counter is live).
   */
  private buildCombatFx(): void {
    const reach = this.player.config.attackHitboxReach;

    const sword = new Graphics();
    sword.rect(-12, -3, 9, 6).fill(0x6b4a2b);                 // handle (brown)
    sword.rect(-3, -8, 5, 16).fill(0xc9a227);                 // guard (gold)
    sword.moveTo(2, -4); sword.lineTo(44, -1.4); sword.lineTo(52, 0);
    sword.lineTo(44, 1.4); sword.lineTo(2, 4); sword.closePath();
    sword.fill(0xeef3fb); sword.stroke({ color: 0x8fa3c4, width: 1.5 }); // steel blade
    sword.visible = false;
    this.swordFx = sword;

    const slash = new Graphics();
    if (reach > 0) {
      const ro = reach + 34, ri = reach * 0.35 + 8, spread = 0.62;
      slash.arc(0, 0, ro, -spread, spread);
      slash.arc(0, 0, ri, spread, -spread, true);
      slash.closePath();
      slash.fill({ color: 0xeaffff, alpha: 0.92 });
      slash.stroke({ color: 0x9fe8ff, width: 3, alpha: 0.9 });
    }
    slash.visible = false;
    this.slashFx = slash;
    this.worldLayer.addChild(slash);
    this.worldLayer.addChild(sword); // sword on top of the slash flash

    const streak = new Graphics();
    streak.moveTo(0, -16); streak.lineTo(-78, -5); streak.lineTo(-78, 5); streak.lineTo(0, 16);
    streak.closePath();
    streak.fill({ color: 0xbfefff, alpha: 0.5 });
    streak.visible = false;
    this.dashFx = streak;
    this.worldLayer.addChild(streak);
  }

  /** Position/orient the COMBAT swing + dash visuals each render frame (render-only; reads the
   *  Player's live attack/dash counters). */
  private renderCombatFx(): void {
    const p = this.player;
    const b = p.body;
    const handX = Math.round(b.x + b.w / 2 + p.facing * b.w * 0.34);
    const handY = Math.round(b.y + b.h * 0.55);
    const aMax = p.config.attackHitboxActiveSteps;
    const swinging = p.attackActiveSteps > 0 && p.attackDir !== null && aMax > 0 && p.dashActiveSteps === 0;
    const prog = swinging ? 1 - p.attackActiveSteps / aMax : 0; // 0..1 across the swing
    const angle = this.combatStrikeAngle(p.attackDir, prog);
    const REST_TILT = 0.12; // resting blade tilts slightly DOWN-forward, never up

    if (this.swordFx) {
      this.swordFx.visible = p.attackEnabled;
      if (p.attackEnabled) {
        this.swordFx.position.set(handX, handY);
        this.swordFx.rotation = swinging ? angle : (p.facing > 0 ? REST_TILT : Math.PI - REST_TILT);
      }
    }
    if (this.slashFx) {
      this.slashFx.visible = swinging;
      if (swinging) {
        this.slashFx.position.set(handX, handY);
        this.slashFx.rotation = angle;
        this.slashFx.alpha = 0.95 - prog * 0.5; // fade out as the swing completes
      }
    }
    if (this.dashFx) {
      const dashing = p.dashActiveSteps > 0;
      this.dashFx.visible = dashing;
      if (dashing) {
        this.dashFx.position.set(Math.round(b.x + b.w / 2), Math.round(b.y + b.h * 0.5));
        this.dashFx.scale.x = p.dashDir;
      }
    }
  }

  /** The blade/crescent angle for a strike, swept across the swing (prog 0→1). Math.PI is a const
   *  (lint-clean — no transcendental call); arc trig is Pixi's, not ours. */
  private combatStrikeAngle(dir: 'left' | 'right' | 'up' | 'down' | null, prog: number): number {
    const sweep = 1.7;            // radians swept over the window
    const HALF_PI = Math.PI / 2;
    switch (dir) {
      case 'right': return -sweep / 2 + sweep * prog;
      case 'left':  return Math.PI + sweep / 2 - sweep * prog;
      case 'down':  return HALF_PI - sweep / 2 + sweep * prog;
      case 'up':    return -HALF_PI + sweep / 2 - sweep * prog;
      default:      return 0;
    }
  }

  private buildGoalSprite(e: LevelEntity, cx: number, feetY: number): void {
    const tex = this.tex.get('goal_flag_a');
    const s = new Sprite(tex);
    s.anchor.set(0.5, 1);
    s.position.set(cx, feetY);
    this.worldLayer.addChild(s);
    this.goalSprite = s;
    void e;
  }

  /** Rebuild the entity sprite array FRESH from `sim.entities` (parallel by index). Called at
   *  load and on every respawn (via fx.onRespawn) so the sprites track a rebuilt entity list. */
  private rebuildEntitySprites(): void {
    for (const sp of this.entitySprites) sp.destroy();
    this.entitySprites = [];
    for (const e of this.sim.entities) this.addEntitySprite(e);
  }

  /** Push ONE bottom-center sprite for a sim entity (parallel-indexed). Shared by the rebuild +
   *  the runtime-spawn hook (fx.onSpawn). POLISH_02 #4: a TILING_REGION_TYPES entity (LAVA/FAN/
   *  EXITGATE/DREAMBLOCK) gets a TilingSprite so its region art repeats per 64px cell instead of
   *  stretching one tile across the whole zone; every other entity keeps the plain-Sprite path
   *  (untouched, per scope). */
  private addEntitySprite(e: Entity): void {
    const tex = this.tex.get(e.spriteKey()) ?? this.tex.get('goomba_rest');
    const s: Sprite | TilingSprite = TILING_REGION_TYPES.has(e.type)
      ? new TilingSprite({ texture: tex ?? Texture.WHITE, width: 1, height: 1 })
      : new Sprite(tex);
    s.anchor.set(0.5, 1);
    this.entitySprites.push(s);
    this.worldLayer.addChild(s);
  }

  private buildUI(): void {
    this.clearBanner = new Text({
      text: 'LEVEL CLEAR!',
      style: { fill: '#ffe66d', fontFamily: 'system-ui, sans-serif', fontSize: 64, fontWeight: '800', stroke: { color: '#1b2030', width: 8 } },
    });
    this.clearBanner.anchor.set(0.5);
    this.clearBanner.visible = false;
    this.uiLayer.addChild(this.clearBanner);

    this.failBanner = new Text({
      text: 'GAME OVER — press R',
      style: { fill: '#ff6b6b', fontFamily: 'system-ui, sans-serif', fontSize: 56, fontWeight: '800', stroke: { color: '#1b2030', width: 8 } },
    });
    this.failBanner.anchor.set(0.5);
    this.failBanner.visible = false;
    this.uiLayer.addChild(this.failBanner);

    this.flashRect = new Sprite(Texture.WHITE);
    this.flashRect.alpha = 0;
    this.uiLayer.addChild(this.flashRect);
  }

  /**
   * Tier 2 (Feature F): attribute the current death to a tile cell and fire onDeath. Called from
   * fx.onDeath (the real-death sim paths) BEFORE respawn. Best-effort: a missing hook is a no-op. */
  private reportDeath(cause: DeathCause): void {
    if (!this.onDeath) return;
    const b = this.player.body;
    const c = deathCell(b.x, b.w, b.y, b.h, this.grid.ts, this.grid.cols, this.grid.rows, cause);
    this.onDeath(c.x, c.y, c.cause);
  }

  /** Respawn (death / fall / hazard / timer / R / load). Gameplay reset lives in the Sim; the
   *  render reset + sprite re-sync + camera snap are in fx.onRespawn. */
  respawn(): void {
    this.sim.respawn();
  }

  /** Category readout for the HUD / leaderboard (Tier 3, Feature H). */
  mutatorLabel(): string { return this.mutators.label(); }
  /** The active mutator ids (sorted) — what a Replay records + boards key on. */
  get mutatorIds(): readonly MutatorId[] { return this.mutators.ids; }
  /** Authored movement archetype id — recorded on a Replay + carried in the board key. */
  get archetypeId(): ArchetypeId { return this.archetype.id; }
  /** Archetype label for the HUD / leaderboard category. */
  archetypeLabel(): string { return this.archetype.label; }

  // ── Fixed-step update — delegate to the Sim, then follow the camera ───────────
  // The deterministic step (+ its death POLICY, via the Sim's deathPolicy + fx.onFreeze) lives in
  // the Sim now. World only early-outs on the terminal states it owns the render of, drives the
  // step, and eases the camera to the player (render).
  update(dt: number, input: InputSnapshot): void {
    if (this.sim.cleared || this.failed) return;
    this.sim.step(dt, input);
    const p = this.player.body;
    // POLISH_01 #3: pass velocity + facing so the camera leads in the travel direction and eases
    // X (render-only; the sim never reads the camera → replay parity is unaffected).
    this.camera.follow(p.x + p.w / 2, p.y + p.h / 2, 0.15, this.player.vx, this.player.facing);
    // POLISH_01 #5: audible low-time warning on a timed level.
    this.tickLowTimeWarning();
  }

  /** POLISH_01 #5: beep once per whole second in the final 10 s of a timed level. Deterministic
   *  (keyed off the fixed-step countdown) and audio is inert, so replay parity is unaffected. */
  private tickLowTimeWarning(): void {
    if (!this.hasTimer) return;
    const left = this.timeRemainingStepsLeft;
    if (left > 600 || left <= 0) { this.lastTickSecond = -1; return; }
    const sec = Math.ceil(left / 60);
    if (sec !== this.lastTickSecond) { this.lastTickSecond = sec; this.sound.play('bump', 0.22, 1.4); }
  }

  // ── HUD readouts (Brief 013) — text-only ────────────────────────────────────
  private get progress(): ClearProgress {
    return {
      coinsCollected: this.coins, placedCoinsCollected: this.placedCoinsCollected, totalCoins: this.totalCoins,
      enemiesKilled: this.enemiesKilled, totalEnemies: this.totalEnemies,
    };
  }
  /** Condition + progress label, e.g. "Coins 3/7" / "Enemies 2/4" / "Reach goal". */
  clearConditionLabel(): string { return conditionLabel(this.sim.clearCondition, this.progress); }
  /** True when this level has a countdown timer. */
  get hasTimer(): boolean { return this.timeLimitSteps > 0; }
  /** Remaining time as MM:SS (only meaningful when hasTimer). */
  timeRemainingLabel(): string {
    return formatTime(timeRemainingSteps(this.timeLimitSteps, this.stepCount));
  }
  /** Steps left in the countdown (for a low-time warning / tests). */
  get timeRemainingStepsLeft(): number {
    return timeRemainingSteps(this.timeLimitSteps, this.stepCount);
  }

  // ── Variable-rate render ────────────────────────────────────────────────────
  render(_alpha: number, dtSec = 1 / 60): void {
    // Advance render-only juice on real wall-clock dt (frame-rate-independent decay).
    this.juice.update(dtSec);
    this.particles.update(dtSec);
    this.filterFx.update(dtSec);
    // POLISH_01 #2: spawn the queued death-site burst now — AFTER the in-step respawn's
    // particles.clear() ran, so it survives into the frozen hitstop frame shown at the death site.
    if (this.pendingDeathBurst) {
      this.particles.burst(this.pendingDeathBurst.x, this.pendingDeathBurst.y, 16, 0xff3b46);
      this.pendingDeathBurst = null;
    }
    this.worldLayer.x = Math.round(-this.camera.x + this.juice.shakeX);
    this.worldLayer.y = Math.round(-this.camera.y + this.juice.shakeY);
    this.filterFx.setViewport(-this.worldLayer.x, -this.worldLayer.y, this.app.screen.width, this.app.screen.height);
    if (this.sky) this.sky.tilePosition.x = -this.camera.x * SKY_PARALLAX; // POLISH_02 #7
    if (this.bg) this.bg.tilePosition.x = -this.camera.x * this.tunables.bgParallax;

    // player sprite
    // POLISH_02 #11: key the flag wave to real dt (was a per-render constant → 2.4x faster at 144Hz).
    this.flagAnim += dtSec * 3;
    const pb = this.player.body;
    const ps = this.playerSprite;
    ps.texture = this.tex.get(this.playerSpriteKey()) ?? ps.texture;
    const sc = this.player.form.spriteScale;
    ps.scale.set(this.player.facing * sc * this.juice.squashX, sc * this.juice.squashY);
    ps.tint = this.juice.playerTint();
    ps.position.set(Math.round(pb.x + pb.w / 2), Math.round(pb.y + pb.h + this.tunables.playerFootOffset));
    if (this.player.dashActiveSteps > 0) {
      this.afterTick ^= 1;
      if (this.afterTick === 0) this.particles.afterImage(ps.texture, ps.x, ps.y, ps.scale.x, ps.scale.y);
    }
    // POLISH_01 #8: ongoing feet dust while skidding (the onset screech is edge-fired from the sim).
    if (this.player.skidding) {
      this.skidTick++;
      if (this.skidTick % 6 === 0) this.particles.dust(pb.x + pb.w / 2, pb.y + pb.h, 3, -this.player.facing);
    } else {
      this.skidTick = 0;
    }
    if (this.invulnSteps > 0) ps.visible = Math.floor(this.invulnSteps / 4) % 2 === 0;
    else if (this.iframeTimer > 0) ps.visible = Math.floor(this.iframeTimer * 12) % 2 === 0;
    else ps.visible = true;

    // COMBAT swing/dash visuals (render-only).
    this.renderCombatFx();

    // entities (generic — keys/visibility come from each entity)
    const entities = this.sim.entities;
    for (let i = 0; i < entities.length; i++) {
      const e = entities[i];
      const s = this.entitySprites[i];
      if (!s) continue;
      s.texture = this.tex.get(e.spriteKey()) ?? s.texture;
      s.visible = e.visible();
      const rs = e.renderSize?.();
      if (rs) {
        s.width = rs.w;
        s.height = rs.h;
        // POLISH_02 #4: a TilingSprite region entity repeats its art once per 64px grid cell —
        // tileScale maps the CURRENT texture's native px to one grid cell (ts), so a 2-frame
        // anim's texture swap (lava/fan) keeps tiling correctly even if a fallback/placeholder
        // texture has a different native size than the vendored art.
        if (s instanceof TilingSprite) {
          const ts = this.grid.ts;
          const tx = s.texture;
          if (tx.width > 0 && tx.height > 0) s.tileScale.set(ts / tx.width, ts / tx.height);
        }
        if (e.facing === -1) s.scale.x = -Math.abs(s.scale.x);
      } else {
        const basis = e.spriteScaleBasis ?? e.body.h / 64;
        const escale = basis * 1.15;
        const artDir = e.spriteFacesLeft ? -1 : 1;
        s.scale.set(artDir * e.facing * escale, escale);
      }
      const yOff = e.renderYOffset?.() ?? 0;
      const padY = rs ? 0 : 4;
      s.position.set(Math.round(e.body.x + e.body.w / 2), Math.round(e.body.y + e.body.h + padY + yOff));
    }

    // goal flag wave (2-frame)
    if (this.goalSprite) {
      this.goalSprite.texture = this.tex.get(Math.floor(this.flagAnim) % 2 === 0 ? 'goal_flag_a' : 'goal_flag_b')!;
    }

    // UI banners centered on screen
    this.clearBanner.position.set(this.app.screen.width / 2, this.app.screen.height / 2);
    this.failBanner.position.set(this.app.screen.width / 2, this.app.screen.height / 2);

    this.flashRect.width = this.app.screen.width;
    this.flashRect.height = this.app.screen.height;
    this.flashRect.alpha = this.juice.screenFlash;
  }

  private playerSpriteKey(): string {
    const set = this.player.form.spriteSet;
    const st = this.player.state;
    if (st === 'jump' || st === 'fall') return `${set}_jump`;
    if (st === 'duck') return `${set}_duck`;
    if (st === 'skid') return `${set}_duck`; // POLISH_01 #8: duck reads GROUNDED (jump read as mid-air)
    if (st === 'walk' || st === 'run') return this.player.walkFrameA ? `${set}_walk_1` : `${set}_walk_2`;
    return `${set}_idle`;
  }

  resize(): void {
    this.camera.resize(this.app.screen.width, this.app.screen.height);
    if (this.bg) {
      this.bg.width = this.app.screen.width;
      this.bg.height = this.app.screen.height;
      const far = this.tex.get('bg_far');
      if (far) this.bg.tileScale.set(this.app.screen.height / far.height);
    }
    if (this.sky) { // POLISH_02 #7
      this.sky.width = this.app.screen.width;
      this.sky.height = this.app.screen.height;
      const sky = this.tex.get('bg_sky');
      if (sky) this.sky.tileScale.set(this.app.screen.height / sky.height);
    }
  }

  /**
   * Tear down everything load() added so a host (the editor) can swap back to edit mode on the
   * SAME Pixi Application with no leaks. Bundle textures stay cached for instant re-entry.
   */
  destroy(): void {
    this.app.stage.removeChild(this.bgLayer, this.worldLayer, this.uiLayer);
    this.bgLayer.destroy({ children: true });
    this.worldLayer.destroy({ children: true });
    this.uiLayer.destroy({ children: true });
    this.tileSprites = [];
    this.decorSprites = []; // POLISH_02 #6
    this.entitySprites = [];
    this.sound.destroy();
  }

  // ── Replay / ghost support (Tier 1, Phase A) ─────────────────────────────────
  /** Content id of the level this World runs — what replays / PBs / runs key on. */
  get levelContentId(): string { return computeLevelId(this.level); }

  /** Remove this World's three render layers from the stage so it runs as a PURE sim (an
   *  off-screen ghost World). Also mutes its SFX. */
  detachFromStage(): void {
    this.app.stage.removeChild(this.bgLayer, this.worldLayer, this.uiLayer);
    this.sound.setMuted(true);
  }

  /** Add an extra sprite to THIS World's world layer so it shares the live camera transform. */
  attachOverlaySprite(sprite: Sprite): void { this.worldLayer.addChild(sprite); }

  /** Logical (skin-resolved) texture for a key, or undefined. */
  textureFor(key: string): Texture | undefined { return this.tex.get(key); }

  /** The player sprite key for the CURRENT player state (public, for a ghost renderer). */
  currentPlayerSpriteKey(): string { return this.playerSpriteKey(); }
}
